import { AlertIcon } from '@approvalmax/ui';
import { Box, Divider, Flex, Grid, Label, Text, Tooltip } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import range from 'lodash/range';
import { memo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { t } from 'services/localization';

import { WalletsTotalsProps } from './WalletsTotals.types';

export const WalletsTotals = memo<WalletsTotalsProps>((props) => {
    const { lineItemTotalsGroupedByWallet, hideBalances, wallets, isLoadingWallets, isLoadingQuotes, total } = props;

    return (
        <Box spacing='0 60'>
            <Flex wrap justifyContent='end' spacing='4 100'>
                {!hideBalances && (
                    <Grid gap={4}>
                        {isLoadingWallets
                            ? range(0, lineItemTotalsGroupedByWallet.length ?? 3).map((index) => (
                                  <Grid
                                      key={index}
                                      gridTemplateColumns='max-content minmax(124px, max-content)'
                                      gap={24}
                                  >
                                      <Label size='small' spacing='0'>
                                          {t('balance_wallet_var_currency', {
                                              currency: <Skeleton inline width={30} height={12} />,
                                          })}
                                      </Label>

                                      <Text font='label' fontSize='small' color='midnight100' textAlign='right'>
                                          <Skeleton width={100} height={12} />
                                      </Text>
                                  </Grid>
                              ))
                            : wallets.map(({ id, balance, wallet }) => (
                                  <Grid key={id} gridTemplateColumns='max-content minmax(124px, max-content)' gap={24}>
                                      <Label size='small' spacing='0'>
                                          {t('balance_wallet_var_currency', { currency: id })}
                                      </Label>

                                      <Text font='label' fontSize='small' color='midnight100' textAlign='right'>
                                          {wallet.isVisible ? intl.formatCurrency(balance, id) : t('not_available')}
                                      </Text>
                                  </Grid>
                              ))}
                    </Grid>
                )}

                <Grid gap={4}>
                    {lineItemTotalsGroupedByWallet.map(({ currency, total, totalFee }) => (
                        <Grid key={currency} gridTemplateColumns='auto minmax(124px, max-content)' gap={24}>
                            <Label size='small' spacing='0'>
                                {t('final_total_amount_in_var_currency_including', {
                                    currency,
                                    feeAmount: isLoadingQuotes ? <Skeleton inline width={30} height={12} /> : totalFee,
                                })}
                            </Label>

                            <Tooltip
                                disabled={isLoadingQuotes}
                                activator={
                                    <Text font='label' fontSize='small' color='midnight100' textAlign='right'>
                                        {isLoadingQuotes ? (
                                            <Skeleton inline width={60} height={12} />
                                        ) : (
                                            intl.formatCurrency(total, currency)
                                        )}
                                    </Text>
                                }
                            >
                                <Box maxWidth={300}>{t('this_amount_includes_an_estimated_fee_based_on')}</Box>
                            </Tooltip>
                        </Grid>
                    ))}
                </Grid>
            </Flex>

            {typeof total === 'number' && (
                <>
                    <Grid gridTemplateColumns='420px' justifyContent='end'>
                        <Divider color='midnight80' spacing='8 0' />
                    </Grid>

                    <Grid gridTemplateColumns='auto 152px' gap={24} justifyContent='end' alignItems='center'>
                        <Flex spacing='8'>
                            <AlertIcon size={16} />

                            <Text font='label' color='midnight70'>
                                {t('the_total_amount_is_approximate_and_can_changed')}
                            </Text>
                        </Flex>

                        <Text font='label' fontWeight='bold' textAlign='right'>
                            {total}
                        </Text>
                    </Grid>
                </>
            )}
        </Box>
    );
});

WalletsTotals.displayName = 'WalletsTotals';
